<!--
  Regular-community channel header. Two rows: the channel name on its own line, then the
  control strip — search, filters, community settings, feed layout — which the community
  shell projects its half of into. The strip is sticky (see `.channel-toolbar`), so it
  stays reachable however far down the feed you are; the title scrolls away with the rest.
-->
@if (postType == "channels" && !betaStringId) {
  <div class="channel-title-row">
    <div class="channel-title">
      <fui-heading [text]="(selectedChannel?.title | localize: selectedChannel?.string_id || '') || ''" [level]="2" fontSize="f7"></fui-heading>
    </div>
    @if (currentAlgoliaQuery && algoliaResultCount) {
      <span class="t-14 channel-results-count">{{ algoliaResultCount }} results found</span>
    }
  </div>
  <div class="channel-toolbar">
    <ng-content select="[channelToolbar]"></ng-content>
    <!--
      Feed layout toggle. Beta keeps its own list/detailed control in the filter sidebar
      (`selectedBetaView`), which is why this whole branch is non-beta only — two controls
      driving the same `displayType` would fight each other.
    -->
    <div class="fui-view-toggle" role="group" aria-label="Feed layout">
      <button type="button" [class.active]="displayType == 'detailed'" (click)="selectViewMode('detailed')" aria-label="Detailed view" placement="bottom" [ngbTooltip]="'Detailed view'">
        <fa-icon icon="tv"></fa-icon>
      </button>
      <button type="button" [class.active]="displayType == 'list'" (click)="selectViewMode('list')" aria-label="List view" placement="bottom" [ngbTooltip]="'List view'">
        <fa-icon icon="list"></fa-icon>
      </button>
    </div>
  </div>
}
<!-- Beta channels and every topic keep the original one-line header: neither has a control
     strip to carry (beta filters live in its sidebar; topics have no search). -->
@if (betaStringId || postType == "topics") {
  <div class="d-flex d-md-flex justify-space-between align-items-center m-b-10 channel-header-row">
    @if (postType == "channels") {
      <div class="channel-title">
        <fui-heading [text]="(selectedChannel?.title | localize: selectedChannel?.string_id || '') || ''" [level]="2" fontSize="f7"></fui-heading>
      </div>
      @if (currentAlgoliaQuery && algoliaResultCount) {
        <span class="t-14 channel-results-count">{{ algoliaResultCount }} results found</span>
      }
    }
    @if (postType == "topics") {
      <div class="channel-title">
        <fui-heading [text]="selectedTopic.title" [level]="2" fontSize="f7"></fui-heading>
        <span class="t-14">Added on {{ topicStartDate | date }}</span>
        @if (selectedTopic.archived) {
          <span>&nbsp;&nbsp;&bull;&nbsp;&nbsp;</span>
        }
        @if (selectedTopic.archived && topicArchivedDate) {
          <span class="t-14 c-red">Archived on {{ topicArchivedDate | date }}</span>
        }
      </div>
    }
  </div>
}
@if (selectedChannel && communityService.channelSplashes[selectedChannel.string_id] && !betaStringId) {
  <div class="channel-slug" [innerHTML]="communityService.channelSplashes[selectedChannel.string_id] | safe: 'html'"></div>
}
@if (selectedTopic && selectedTopic.description) {
  <div class="topic-slug" [innerHTML]="selectedTopic.description | safe: 'html'"></div>
}
<div class="posts p-x-5">
  @if ((!communityService.profile || !communityService.profile.status || communityService.profile.status == "active") && (selectedChannel || (selectedTopic && !selectedTopic.archived))) {
    <myp-create-post-input #createPostInput [selectedChannel]="selectedChannel" [topic]="selectedTopic" [betaStringId]="betaStringId" [communityStringId]="communityStringId"></myp-create-post-input>
  }
  <div class="m-t-20">
    @if (createPostInput?.queuedPosts?.length > 0) {
      @for (queuedPost of createPostInput.queuedPosts; track queuedPost) {
        <div>
          @if (!queuedPost.scheduled_date) {
            <myp-comment [comment]="queuedPost" [isCommunityPost]="true" [queued]="true" [displayedSection]="postType"></myp-comment>
          }
        </div>
      }
    }
    @if (communityService.pendingPosts) {
      @for (pendingPost of communityService.pendingPosts; track pendingPost) {
        <div>
          @if (!pendingPost.scheduled_date) {
            <myp-comment [comment]="pendingPost" [isCommunityPost]="true" [queued]="true" [displayedSection]="postType"></myp-comment>
          }
        </div>
      }
    }
    @if (betaStringId && (betaCommunityService.showBetaType || betaCommunityService.showBetaStatus || betaCommunityService.showBetaReviewStatus || betaCommunityService.showBetaAuthor)) {
      <div style="margin: 10px auto">
        Filters Applied:
        @if (betaCommunityService.showBetaType) {
          <span class="item-tag t-12 font-weight-bold">Type: {{ betaCommunityService.betaFilterReadableValues[betaCommunityService.showBetaType] || betaCommunityService.showBetaType }}</span>
        }
        @if (betaCommunityService.showBetaStatus) {
          <span class="item-tag t-12 font-weight-bold">Status: {{ betaCommunityService.betaFilterReadableValues[betaCommunityService.showBetaStatus] || betaCommunityService.showBetaStatus }}</span>
        }
        @if (betaCommunityService.showBetaReviewStatus) {
          <span class="item-tag t-12 font-weight-bold">
            Review Status: {{ betaCommunityService.betaFilterReadableValues[betaCommunityService.showBetaReviewStatus] || betaCommunityService.showBetaReviewStatus }}
          </span>
        }
        @if (betaCommunityService.showBetaAuthor && communityService.betaAuthorMap && communityService.betaAuthorMap[betaCommunityService.showBetaAuthor]) {
          <span class="item-tag t-12 font-weight-bold">Author: {{ communityService.betaAuthorMap[betaCommunityService.showBetaAuthor].name }}</span>
        }
        &nbsp;&nbsp;
        <span class="small-btn" (click)="resetFilters.next(true)">Reset Filters</span>
      </div>
    }
    <!-- Placeholders shaped like the layout about to render, so posts fill in where the
         skeletons were instead of replacing a spinner and pushing the page around. -->
    @if (loading) {
      <div class="m-y-10" role="status" aria-live="polite">
        <span class="sr-only">Loading posts…</span>
        @if (displayType == "list" && !betaStringId) {
          <myp-skeleton-loader type="community-post-row" [number]="6"></myp-skeleton-loader>
        } @else if (displayType == "list") {
          <myp-skeleton-loader type="list-item" [number]="8" extraContainerClass="m-y-20"></myp-skeleton-loader>
        } @else {
          <myp-skeleton-loader type="carousel" height="220px" borderRadius="16px" extraContainerClass="m-b-20"></myp-skeleton-loader>
          <myp-skeleton-loader type="carousel" height="320px" borderRadius="16px" extraContainerClass="m-b-20"></myp-skeleton-loader>
          <myp-skeleton-loader type="carousel" height="180px" borderRadius="16px"></myp-skeleton-loader>
        }
      </div>
    }

    @if (!loading) {
      @if (displayType == "detailed") {
        <ul class="list-group">
          @for (post of communityService.activeParentPosts; track post; let i = $index) {
            @if ((post.scheduled_date && selectedChannel.id === "my_posts" && post.author.id == user?.id) || !post.scheduled_date) {
              <li
                class="list-group-item border-0 p-0"
                [class.bg-transparent]="post.id !== highlightPostId"
                [class.highlight-flash]="post.id === highlightPostId"
                [class.community-post-unread]="unreadPostIds[post.id]"
                mypSeenInView
                [seenEnabled]="!!unreadPostIds[post.id]"
                (seen)="onPostSeenInFeed(post.id)"
              >
                <!-- The detailed feed has no "open" to mark a post read, so reading it on screen
                     does: see `onPostSeenInFeed`. -->
                <myp-comment
                  [betaStringId]="betaStringId"
                  [communityStringId]="communityStringId"
                  [comment]="post"
                  [isCommunityPost]="true"
                  [replies]="communityService.activeParentThreadMap[post.key]"
                  (postEdited)="initPosts()"
                  [index]="i"
                  (channelSelected)="channelSelected.next($event)"
                  (topicSelected)="topicSelected.next($event)"
                  [displayedSection]="postType"
                ></myp-comment>
              </li>
            }
          }
        </ul>
      }
      <!-- Community list view — the mobile card ported to web. Beta keeps its own
           `myp-list-item` layout below; the two are deliberately different designs. -->
      @if (displayType == "list" && !betaStringId) {
        <ul class="list-group community-post-list">
          @for (post of communityService.activeParentPosts; track post) {
            @if ((post.scheduled_date && selectedChannel.id === "my_posts" && post.author.id == user?.id) || !post.scheduled_date) {
              <li class="list-group-item border-0 p-0 bg-transparent" [class.highlight-flash]="post.id === highlightPostId" [class.community-post-unread]="unreadPostIds[post.id]">
                <myp-community-post-list-item
                  [post]="post"
                  [communityStringId]="communityStringId"
                  [replies]="communityService.activeParentThreadMap[post.key]"
                  [splitPaneActive]="splitPaneActive"
                  [compact]="compactList"
                  [class.active]="post.id === selectedPostId"
                  (openPost)="onPostOpened($event)"
                ></myp-community-post-list-item>
              </li>
            }
          }
        </ul>
      }
      @if (displayType == "list" && betaStringId) {
        <div>
          <ul class="list-group">
            @for (post of communityService.activeParentPosts; track post; let i = $index) {
              <li class="list-group-item border-0 p-0" [class.bg-transparent]="post.id !== highlightPostId" [class.highlight-flash]="post.id === highlightPostId">
                <myp-list-item
                  [photo]="post.author.photo ? post.author.photo : './assets/images/default_avatar.jpg'"
                  [title]="appService.decodeHtmlCharacters(helperService.stripHtml(post.subject, true))"
                  [subData]="[(post.created.toDate() | date: 'MMM Do [at] hh:mm' : true) + ' by ' + post.author.name, getBetaItemStats(post), getChannelString(post.channels)]"
                  [item]="post"
                  [alignTop]="true"
                  [href]="env.fenderUrl + (betaStringId ? 'beta/betapost/' + post.id + '?betaStringId=' + betaStringId : 'community/post/' + post.id)"
                  (itemClicked)="goToPost(post)"
                >
                  <div class="button-content">
                    <button class="fa-button m-r-20" tabindex="-1" (click)="$event.stopPropagation(); $event.preventDefault(); copyLink(post, $event)">
                      <fa-icon aria-label="Copy" icon="copy" ngbTooltip="Copy Link"></fa-icon>
                    </button>
                  </div>
                </myp-list-item>
                @if (post.highlightSnippet) {
                  <div class="algolia-search-snippet m-l-40 m-b-10 m-t-10" [innerHTML]="post.highlightSnippet | safe: 'html'"></div>
                }
                <hr />
              </li>
            }
          </ul>
        </div>
      }
      @if (!communityService.activePostsLimitReached) {
        <div class="text-center m-y-30">
          @if (!pagingNeedsSubscription) {
            <button type="button" class="btn btn-transparent small-btn" [disabled]="loadingMore" [attr.aria-busy]="loadingMore" (click)="usingAlgolia ? showMoreAlgolia() : showMore()">
              @if (loadingMore) {
                <fa-icon icon="spinner" [animation]="'spin'" class="m-r-5" aria-hidden="true"></fa-icon>
              }
              {{ "com.show_more_posts" | translate }}
            </button>
          }
          @if (pagingNeedsSubscription) {
            <button type="button" class="btn btn-transparent small-btn" (click)="upgrade()">{{ "com.upgrade_more_posts" | translate }}</button>
          }
        </div>
      }
      @if (communityService.activeParentPosts && !communityService.activeParentPosts.length) {
        <div class="text-center m-y-20 gothic">
          <span>
            {{ "com.no_posts_found" | translate: { channel: selectedChannel ? (selectedChannel.title | localize: selectedChannel.string_id) : selectedTopic.title } }}
          </span>
        </div>
      }
    }
  </div>
</div>
